import {injectable, inject} from 'inversify'
import {action, computed, makeObservable, observable, runInAction} from 'mobx'
import i18next from 'i18next'

import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'
import {LoadingState} from '@/types'

import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {IAIChatMessengerStore} from './IAIChatMessengerStore'
import {generateAiChatToken, createSendbirdUser} from './services/fetchers'
import {AI_CHAT_CONSTANTS} from './constants'

@injectable()
export class AIChatMessengerStore implements IAIChatMessengerStore {
    loadingState: LoadingState = LoadingState.IDLE
    authToken: string | null = null
    authTokenExpiresAt: number | null = null
    _profileStore: ProfileStoreInterface

    constructor(@inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface) {
        makeObservable(this, {
            loadingState: observable,
            authToken: observable,
            authTokenExpiresAt: observable,
            isAuthenticated: computed,
            initializeAuth: action.bound,
            refreshAuthToken: action.bound,
            clearAuth: action.bound
        })

        this._profileStore = profileStore
    }

    get isAuthenticated(): boolean {
        return !!(this.authToken && this.authTokenExpiresAt && Date.now() < this.authTokenExpiresAt)
    }

    initializeAuth = async (): Promise<void> => {
        if (this.isAuthenticated) {
            return
        }

        await this.refreshAuthToken()
    }

    refreshAuthToken = async (): Promise<void> => {
        runInAction(() => {
            this.loadingState = LoadingState.LOADING
        })

        try {
            const userPublicId = this._profileStore?.profileData?.publicId
            const userNickname =
                this._profileStore?.profileData?.firstName &&
                this._profileStore?.profileData?.lastName
                    ? `${this._profileStore.profileData.firstName} ${this._profileStore.profileData.lastName}`.trim()
                    : userPublicId

            if (!userPublicId) {
                throw new Error(i18next.t('AIChatMessenger.errors.userPublicIdNotAvailable'))
            }

            const createUserResult = await createSendbirdUser(
                userPublicId,
                userNickname || userPublicId
            )

            if (createUserResult.error) {
                console.warn('Failed to create Sendbird user:', createUserResult.error)
            }

            const {result, error} = await generateAiChatToken(userPublicId)

            if (error || !result) {
                throw new Error(error?.message)
            }

            runInAction(() => {
                this.authToken = result.token
                this.authTokenExpiresAt = Date.now() + AI_CHAT_CONSTANTS.TOKEN_EXPIRY_MS
                this.loadingState = LoadingState.DONE
            })
        } catch (error) {
            captureSentryError({
                name: 'AIChatMessengerStore refreshAuthToken Error',
                description: getErrorMessage(error, 'Failed to refresh auth token'),
                fingerprint: ['ai-chat-messenger-store-refresh-auth-token'],
                error
            })
            runInAction(() => {
                this.loadingState = LoadingState.FAILED
                this.clearAuth()
            })

            throw error
        }
    }

    clearAuth = (): void => {
        this.authToken = null
        this.authTokenExpiresAt = null
        this.loadingState = LoadingState.IDLE
    }
}
